import React from 'react';
import { SubtractUniversal } from '@duxton-web/icons';
import { Tag, Tooltip } from '@duxton-web/ui';

import ExperimentListingActions from './actions';
import { ObjectInfo, SecondaryText } from '@/common/components';
import { OBJECTDETAILPAGE_OBJECTTYPE } from '@/common/components/ObjectDetailPage/ObjectDetailPage';
import { ABIcon } from '@/common/Icons/ABIcon';
import { AdaptiveIcon } from '@/common/Icons/AdaptiveIcon';
import { ADAPTIVE } from '@/constants';
import { SuperExperiment } from '@/lib/App';
import { AdaptiveTrialsCount } from '@/pages/ExperimentPageV2/AdaptiveDetailSubPage/AdaptiveDetail';
import {
  renderCityColumn,
  renderCountryColumn,
  renderDateColumn,
  renderDurationColumn,
  renderOwnerColumn,
  renderProductUser,
  renderStatusColumn,
  renderVariablesColumn,
} from '@/pages/ExperimentPageV2/common/DashboardColumn';
import {
  DisplayBackendService,
  DisplayExperimentLabel,
  EventCount,
  sortByDate,
  sortString,
  truncate,
} from '@/pages/ExperimentPageV2/helper/utils';

export const ExperimentNameColumn: any = {
  key: 'name',
  title: 'Name',
  dataIndex: 'name',
  fixed: 'left',
  width: 400,
  sorter: (a: SuperExperiment, b: SuperExperiment) => {
    // eslint-disable-next-line no-unsafe-optional-chaining, @typescript-eslint/no-unused-vars
    const [_, experimentNameA] = a.name?.split('.');
    // eslint-disable-next-line no-unsafe-optional-chaining, @typescript-eslint/no-unused-vars
    const [__, experimentNameB] = b.name?.split('.');
    return experimentNameA?.localeCompare(experimentNameB, 'en', {
      numeric: true,
      sensitivity: 'base',
      ignorePunctuation: true,
    });
  },
  render: (name: string, record: SuperExperiment) => {
    let experimentName = truncate(name?.split('.').splice(1).join('.'));
    if (!experimentName) {
      experimentName = record?.name;
    }
    return (
      <ObjectInfo
        objectType={OBJECTDETAILPAGE_OBJECTTYPE.EXPERIMENT}
        icon={record.type == ADAPTIVE ? <AdaptiveIcon /> : <ABIcon />}
        name={experimentName}
        altNameTooltip={name}
        nameShowTooltipWithChildren={false}
        description={
          <div>
            <SecondaryText style={{ marginRight: '0.25rem' }}>
              {record?.type == ADAPTIVE ? 'Adaptive' : 'A/B Test'}
            </SecondaryText>
            {record?.type == ADAPTIVE ? (
              <Tooltip title="Feature is released to a small group for testing, by invitation only.">
                <AdaptiveTrialsCount experimentName={experimentName} />
                <Tag size="xsmall" color="#136FD8">
                  Closed Beta
                </Tag>
              </Tooltip>
            ) : (
              ''
            )}
            {record?.isOverlap && (
              <Tag size="xsmall" color="blue" bordered={false}>
                Overlapping
              </Tag>
            )}
          </div>
        }
        showDescriptionTooltip={false}
      />
    );
  },
};

export const ExperimentCreatedAt: any = {
  key: 'createdAt',
  title: 'Created At',
  dataIndex: 'state',
  width: 280,
  sorter: (a: SuperExperiment, b: SuperExperiment) =>
    sortString(a?.created_at.toString(), b?.created_at.toString()),
  render: (_: string, record: SuperExperiment) =>
    renderDateColumn('createdAt', record),
};

export const ExperimentStatus: any = {
  key: 'status',
  title: 'Status',
  dataIndex: 'state',
  width: 280,
  sorter: (a, b) => sortString(a.state, b.state),
  defaultSortOrder: 'descend',
  render: (state: string) => renderStatusColumn(state),
};

export const ExperimentEvents: any = {
  key: 'events',
  title: 'Events (Last 7 Days)',
  dataIndex: 'name',
  width: 280,
  render: (name: string, record: SuperExperiment) => {
    if (record.type === ADAPTIVE) return <SubtractUniversal />;
    return <EventCount experimentName={name} />;
  },
};

export const ExperimentOwner: any = {
  key: 'owner',
  title: 'Owner',
  dataIndex: 'owner',
  width: 280,
  sorter: (a, b) => sortString(a.owner, b.owner),
  render: (name: string) => renderOwnerColumn(name),
};

export const ExperimentStartDate: any = {
  key: 'startDate',
  title: 'Start Date',
  width: 280,
  sorter: (a, b) => sortByDate(a?.start_time, b?.start_time),
  render: (_: string, record: SuperExperiment) =>
    renderDateColumn('start_time', record),
};

export const ExperimentEndDate: any = {
  key: 'endDate',
  title: 'End Date',
  width: 280,
  sorter: (a, b) => sortByDate(a?.end_time, b?.end_time),
  render: (_: string, record: SuperExperiment) =>
    renderDateColumn('end_time', record),
};

export const ExperimentDuration: any = {
  key: 'duration',
  title: 'Duration',
  dataIndex: 'state',
  width: 280,
  render: (state: string, record: SuperExperiment) =>
    renderDurationColumn(state, record),
};

export const ExperimentProductManager: any = {
  key: 'productManager',
  title: 'Product Manager',
  dataIndex: 'metadata',
  width: 280,
  sorter: (a, b) =>
    sortString(a.metadata?.productManager, b.metadata?.productManager),
  render: (metadata) => renderProductUser('productManager', metadata),
  hidden: true,
};

export const ExperimentProductAnalyst: any = {
  key: 'productAnalyst',
  title: 'Product Analyst',
  dataIndex: 'metadata',
  width: 280,
  sorter: (a, b) =>
    sortString(a.metadata?.productAnalyst, b.metadata?.productAnalyst),
  render: (metadata) => renderProductUser('productAnalyst', metadata),
  hidden: true,
};

export const ExperimentTags: any = {
  key: 'tags',
  title: 'Tags',
  dataIndex: 'state',
  width: 280,
  render: (_, record: SuperExperiment) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [__, experimentName] = record.name.split('.');
    return <DisplayExperimentLabel experimentName={experimentName} />;
  },
  hidden: true,
};

export const ExperimentDomain: any = {
  key: 'domain',
  title: 'Domain',
  dataIndex: 'domain',
  width: 280,
  sorter: (a, b) => sortString(a.domain, b.domain),
  render: (_, record: any) => {
    if (record?.domain) {
      return record.domain;
    }
    return record?.experiment_template?.domain;
  },
  hidden: true,
};

export const ExperimentVariables: any = {
  key: 'variables',
  title: 'Variables',
  dataIndex: 'variables',
  width: 280,
  render: (variables) => variables && renderVariablesColumn(variables),
};

export const ExperimentCountry: any = {
  key: 'country',
  title: 'Country',
  dataIndex: 'state',
  width: 280,
  render: (_, record: SuperExperiment) => renderCountryColumn(record),
};

export const ExperimentCity: any = {
  key: 'city',
  title: 'City',
  dataIndex: 'state',
  width: 280,
  render: (_, record: SuperExperiment) => renderCityColumn(record),
  hidden: true,
};

export const ExperimentBackendService: any = {
  key: 'backendService',
  title: 'Backend Service',
  dataIndex: 'variables',
  width: 280,
  render: (_, record: SuperExperiment) => (
    <DisplayBackendService record={record} />
  ),
  hidden: true,
};

export function ExperimentTableOptions(
  handleReject: (record: SuperExperiment) => void,
  handleEdit: (record: SuperExperiment) => void,
  handleAbort: (record: SuperExperiment) => void,
  handleClone: (record: SuperExperiment) => void,
  handlePause: (record: SuperExperiment) => void,
  handleApprove: () => void,
): any {
  return {
    key: 'option',
    fixed: 'right',
    align: 'center',
    width: 60,
    onCell: (_) => ({
      onClick: (e) => {
        e.stopPropagation();
      },
    }),
    render: (record: SuperExperiment) => (
      <ExperimentListingActions
        experiment={record}
        handleReject={handleReject}
        handleEdit={handleEdit}
        handleClone={handleClone}
        handleAbort={handleAbort}
        handlePause={handlePause}
        handleApprove={handleApprove}
      />
    ),
  };
}
